// DiaryEditor — composed diary form: date / title / content / mood / privacy
// + photo attach.  Submit handler delegates to the parent so the same
// component can be reused for both create and edit modes.
//
// Submit / Cancel actions live in a docked `PageBottomActionBar` (per
// `mockup/mobile.html §3` + `docs/spec/w2-frontend-components.md §3.2`)
// instead of an inline button row — replaces the banned Material FAB.
//
// W3.1 (Anniversary) — adds the 「來源 → 紀念日」 select on create mode
// (only when the current pair has upcoming anniversaries). Selecting an
// anniversary sets `source_type='anniversary'` + `source_id=<id>` on the
// diary payload; clearing the select falls back to `source_type=null`.
//
// W54.0 A1 — the field state, the draft keys and the two heaviest JSX blocks
// moved out of this file, which was 24,699 B / 537 lines and therefore over
// the `AGENTS.md §3 #10` ceiling for a component this sprint had to change:
//
//   * `lib/diary/formState.ts` — the field state and the three picker queries
//   * `lib/diary/drafts.ts`   — the draft keys, scoped by diary id in edit mode
//   * `DiarySourceFields.tsx` — the two 「來源」 selects
//   * `DiaryPhotoAttach.tsx`  — the attached-photo row
//
// The **only** behaviour change is the draft key: edit-mode drafts are keyed
// `diary-title:edit:<id>` / `diary-content:edit:<id>` instead of by mode
// alone, so a leftover draft from one diary cannot surface in another's
// editor. The route guards the rest — `/diaries/:id/edit` does not mount this
// component until the diary has loaded.

import { useState } from "react";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";

import { PageBottomActionBar } from "@/components/system/PageBottomActionBar";
import type { PageBottomActionBarBack } from "@/components/system/PageBottomActionBar";
import { PageContainer, SectionStack } from "@/components/layout";
import { cn } from "@/lib/utils/cn";
import type { MediaOut, MoodCode } from "@/lib/api/diary";
import type { DiaryCreateInput, DiaryUpdateInput } from "@/lib/validation/diary";
import { MoodEmojiPicker } from "@/components/diary/MoodEmojiPicker";
import { PrivacyToggle } from "@/components/diary/PrivacyToggle";
import { UploadModal } from "@/components/albums/UploadModal";
import { DiarySourceFields } from "@/components/diary/DiarySourceFields";
import { DiaryPhotoAttach } from "@/components/diary/DiaryPhotoAttach";
import { DiaryContentField } from "@/components/diary/DiaryContentField";
import { diaryDateSchema } from "@/lib/validation/diary";
import { FormErrorBanner } from "@/components/auth/FormField";
import { useDiaryFormState, type DiaryFormInitial } from "@/lib/diary/formState";
import { todayISO } from "@/lib/diary/dates";

/** `DiaryCreate.content`'s `max_length`, and `DiaryCreate.title`'s.
 *
 *  W39.66 — both are read out of `backend/docsnew/11-api/openapi.yaml` by
 *  `__tests__/diaryEditorInputLimits.test.tsx`, so a change on the server fails
 *  that test instead of letting the two sides drift. The title was already out
 *  of step: capped at 100 here since W2, while migration `e0f1a2b3c4d5` widened
 *  the column to 200 and nothing on this side followed.
 */
const DIARY_CONTENT_LIMIT = 50_000;
const DIARY_TITLE_LIMIT = 200;

interface DiaryEditorProps {
  initial?: DiaryFormInitial;
  attachedMedia?: MediaOut[];
  busy?: boolean;
  mode: "create" | "edit";
  /**
   * The diary being edited.
   *
   * W54.0 A1 — the draft keys are scoped by it in edit mode, so the editor
   * needs to know which diary it is editing. `null` in create mode, where the
   * draft key is the mode alone.
   */
  diaryId?: string | null;
  /**
   * Optional anniversary id pre-selected from outside (e.g. TodayBanner
   * deep-link). Only honoured in `mode === "create"`.
   */
  initialAnniversaryId?: string | null;
  onSubmit: (payload: DiaryCreateInput | DiaryUpdateInput) => Promise<void>;
  /**
   * W35.6: leave-the-page affordance rendered as a 44pt circular button
   * beside the submit CTA. Required on every host page that docks this
   * editor (create / onboarding), so "save" and "go back" are one tap apart.
   */
  back?: PageBottomActionBarBack;
}

export function DiaryEditor({
  initial,
  attachedMedia = [],
  busy = false,
  mode,
  diaryId = null,
  initialAnniversaryId = null,
  onSubmit,
  back,
}: DiaryEditorProps) {
  const {
    date,
    setDate,
    title,
    setTitle,
    mood,
    setMood,
    isPrivate,
    setIsPrivate,
    media,
    setMedia,
    formError,
    setFormError,
    contentLength,
    setContentLength,
    sourceAnniversaryId,
    setSourceAnniversaryId,
    sourceWishId,
    setSourceWishId,
    albumOptions,
    wishesForSource,
    upcomingAnniversaries,
    titleDraft,
    contentDraft,
  } = useDiaryFormState({ initial, mode, diaryId, attachedMedia, initialAnniversaryId });

  const [uploadOpen, setUploadOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: mode === "create" ? "今天想記錄什麼?" : "",
      }),
    ],
    // W39.74 — the draft is read synchronously and used as the content here,
    // not restored by an effect afterwards. `immediatelyRender: false` makes the
    // editor `null` on the first render, so an effect keyed on "the editor
    // exists" could run before the draft had been read, see an empty string and
    // never run again. Measured in the browser exactly that way: the draft was
    // in sessionStorage and the editor was still empty.
    //
    // `initial.content` wins: editing a saved diary must show the diary, not
    // whatever was typed the last time this editor was opened.
    content: initial?.content || contentDraft.value,
    immediatelyRender: false,
    // W39.66 — the counter needs to move as the person types, which is the one
    // thing `editor.storage.characterCount` would have given us.
    onCreate: ({ editor: created }) => setContentLength(created.getText().length),
    onUpdate: ({ editor: updated }) => {
      setContentLength(updated.getText().length);
      contentDraft.save(updated.getHTML());
    },
  });

  const submit = async () => {
    setFormError(null);
    const dateResult = diaryDateSchema.safeParse(date);
    if (!dateResult.success) {
      setFormError(dateResult.error.issues[0]?.message ?? "日期無效");
      return;
    }
    if (!editor) return;
    const html = editor.getHTML();
    const trimmed = html.replace(/<[^>]+>/g, "").trim();
    if (!trimmed) {
      setFormError("請輸入日記內容");
      return;
    }
    // W39.66 — `DiaryCreate.content` is `max_length=50000`, and the counter
    // below was the only thing referring to that number: Tiptap's
    // `CharacterCount` extension is not a dependency of this project, so
    // `storage.characterCount` is `undefined` and the counter's
    // `?? getText().length` fallback is the real measurement. Counting is not
    // limiting — a diary past the cap was a 422 round trip with the counter
    // cheerfully reading 「50,001 / 50,000」 beside it.
    //
    // Counted on the same text the server sees (`getText()`, not the stripped
    // HTML) so the number on screen and the number that decides are one number.
    const length = editor.getText().length;
    setContentLength(length);
    if (length > DIARY_CONTENT_LIMIT) {
      setFormError(`內容超出 ${DIARY_CONTENT_LIMIT.toLocaleString("en-US")} 字, 請刪減後再儲存`);
      return;
    }
    if (mode === "create") {
      await onSubmit({
        date: dateResult.data,
        title: title.trim() || undefined,
        content: html,
        mood: (mood ?? undefined) as MoodCode | undefined,
        is_private: isPrivate,
        media_ids: media.map((m) => m.id),
        source_type: sourceAnniversaryId
          ? "anniversary"
          : sourceWishId
            ? "wishlist"
            : null,
        source_id: sourceAnniversaryId ?? sourceWishId,
      });
    } else {
      await onSubmit({
        date: dateResult.data,
        title: title.trim() || undefined,
        content: html,
        mood: (mood ?? undefined) as MoodCode | undefined,
        is_private: isPrivate,
      });
    }
    // W39.74 — only once the diary exists on the server. Clearing earlier (on
    // unmount, or on a validation failure) is what would make the draft useless
    // for the case it exists for.
    titleDraft.clear();
    contentDraft.clear();
  };

  const primaryLabel = busy ? "儲存中…" : mode === "create" ? "寫這篇日記" : "更新";

  return (
    // W50.0 B-4 — the root keeps its own `pb-32` (128 px). This component
    // docks the bar itself, so the clearance belongs here and NOT on the
    // host page: `PageContainer` contributes only `py-s-5`, which would leave
    // the last field under the bar on `/diaries/new` and `/diaries/:id/edit`.
    // `dockedBarClearance.test.ts` guards exactly that number.
    <div className="flex flex-col pb-32" data-testid="diary-editor">
      <FormErrorBanner message={formError} />

      <PageContainer vertical="loose">
        <SectionStack density="default">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="diary-date" className="font-label text-ink-2">
            日期
          </label>
          <input
            id="diary-date"
            type="date"
            max={todayISO()}
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={busy}
            data-testid="diary-date"
            className={cn(
              "h-12 rounded-md border border-hairline bg-paper px-3",
              "text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2",
            )}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="diary-title" className="font-label text-ink-2">
            標題 (可選)
          </label>
          <input
            id="diary-title"
            type="text"
            maxLength={DIARY_TITLE_LIMIT}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              titleDraft.save(e.target.value);
            }}
            disabled={busy}
            data-testid="diary-title"
            placeholder="今天的小標題…"
            className="h-12 rounded-md border border-hairline bg-paper px-3 text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2"
          />
        </div>

        {/* W52.1 D-4 — the album sheet. `onUploaded` maps `AlbumItemRead` rows onto
            the editor's `MediaOut[]`; `AlbumItemRead.media` already carries every
            field `MediaOut` has plus a `url`, so this is a narrowing rather than a
            second fetch. */}
        {mode === "create" && albumOptions.length > 0 ? (
          /*
           * W52.1 D-4 — this block used to be `<PhotoUploader>` with **no**
           * `albumOptions`. That component is dual-mode by design (see its own
           * header): given `albumOptions` it posts with an `album_id` and the
           * backend creates an `AlbumItem`; without them it falls back to the W2
           * single-file path and creates a bare `Media` row that belongs to no
           * album. The capability was in the component the whole time — the
           * call site never passed it, so 「add a photo to a diary」 and 「add a
           * photo to 相簿」 were two features that looked identical and were not.
           *
           * Now it is the *same* sheet `/albums/:id` uses, so the album chip
           * row is on screen before a single file is chosen rather than after.
           */
          <DiaryPhotoAttach
            media={media}
            disabled={busy}
            onRemove={(id) => setMedia((prev) => prev.filter((x) => x.id !== id))}
            onAdd={() => setUploadOpen(true)}
          />
        ) : null}

        <DiarySourceFields
          anniversaries={upcomingAnniversaries}
          anniversaryId={sourceAnniversaryId}
          onAnniversaryChange={setSourceAnniversaryId}
          wishes={wishesForSource}
          wishId={sourceWishId}
          onWishChange={setSourceWishId}
          disabled={busy}
        />

        <DiaryContentField
          editor={editor}
          contentLength={contentLength}
          limit={DIARY_CONTENT_LIMIT}
          busy={busy}
        />

        <MoodEmojiPicker
          value={mood}
          onChange={setMood}
          content={editor?.getText() ?? ""}
          title={title}
          disabled={busy}
        />

        <PrivacyToggle value={isPrivate} onChange={setIsPrivate} disabled={busy} />
        </SectionStack>
      </PageContainer>

      <PageBottomActionBar
        primary={{
          label: primaryLabel,
          onClick: () => void submit(),
          loading: busy,
          testId: "diary-save",
        }}
        back={back}
        testId="diary-editor-bottom-bar"
      />

      <UploadModal
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        defaultAlbumId={albumOptions[0]?.id ?? ""}
        albumOptions={albumOptions}
        onUploaded={(items) => {
          // Only the id survives: see `DiaryAttachment`. The album's own grid
          // renders from the same id, so a thumbnail here and a tile there
          // cannot point at different files.
          if (items.length === 0) return;
          setMedia((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            return [
              ...prev,
              ...items
                .map((i) => ({ id: i.media.id }))
                .filter((m) => !seen.has(m.id)),
            ];
          });
        }}
      />
    </div>
  );
}
